import { CinematicModal } from "@/components/ui/CinematicModal";
import { cinematicModalSubtitleClass, cinematicModalTitleClass } from "@/lib/cinematicModalStyles";
import { Info, Lightbulb, MessageCircleMore, Route, Sparkles, Target } from "lucide-react";
import React from "react";

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const learningModes = [
  {
    icon: Target,
    title: "Guided",
    desc: "The tutor explains in your instruction language, then gets you speaking with useful corrections.",
  },
  {
    icon: MessageCircleMore,
    title: "Conversation",
    desc: "More immersion and less hand-holding when you want to keep the conversation in your target language.",
  },
  {
    icon: Route,
    title: "Journey",
    desc: "A structured chapter path for learners who prefer a clear next step instead of an empty prompt.",
  },
];

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  return (
    <CinematicModal isOpen={isOpen} onClose={onClose} maxWidth="2xl" titleId="about-dialog-title">
      <div className="pr-8">
        <h2
          id="about-dialog-title"
          className={`${cinematicModalTitleClass} flex items-center gap-2`}
        >
          <Info className="h-5 w-5 text-orange-300" aria-hidden="true" />
          About ChickyTutor
        </h2>
        <p className={cinematicModalSubtitleClass}>A private tutor that learns how you learn.</p>
      </div>

      <div className="mt-6 space-y-6 text-sm leading-relaxed text-white/75">
        <p>
          Speak, get one useful correction, then try it again better. ChickyTutor adapts the lesson
          to your level, recurring mistakes, and pace without turning practice into an exam.
        </p>

        <section>
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
            <Target className="h-4 w-4 text-orange-300" aria-hidden="true" />
            How it works
          </h3>
          <ol className="grid gap-3 sm:grid-cols-3">
            {["Speak naturally", "Use the correction", "Try it again"].map((step, index) => (
              <li key={step} className="border-l-2 border-orange-300/45 pl-3">
                <span className="block text-[10px] font-bold uppercase text-white/35">
                  Step {index + 1}
                </span>
                <span className="font-semibold text-white/80">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
            <Sparkles className="h-4 w-4 text-orange-300" aria-hidden="true" />
            Learning modes
          </h3>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {learningModes.map((mode) => {
              const Icon = mode.icon;
              return (
                <div key={mode.title} className="grid grid-cols-[1.25rem_5.5rem_1fr] gap-3 py-3">
                  <Icon className="mt-0.5 h-4 w-4 text-orange-300" aria-hidden="true" />
                  <h4 className="font-semibold text-white">{mode.title}</h4>
                  <p className="text-white/60">{mode.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
            <Lightbulb className="h-4 w-4 text-orange-300" aria-hidden="true" />
            Pro tips
          </h3>
          <div className="space-y-3 text-white/65">
            <p>
              <span className="font-semibold text-orange-200">Consistent practice:</span> ten
              minutes daily beats one long session once a week.
            </p>
            <p>
              <span className="font-semibold text-orange-200">Embrace mistakes:</span> corrections
              are part of the lesson, not a score against you.
            </p>
            <p>
              <span className="font-semibold text-orange-200">Level progression:</span> start where
              you are comfortable, then let the tutor increase challenge gradually.
            </p>
          </div>
        </section>

        <p className="border-t border-white/10 pt-4 text-center text-xs text-white/45">
          The tutor remembers the wobble. You keep talking.
        </p>
      </div>
    </CinematicModal>
  );
};

export default AboutModal;
